import { useCallback, useMemo, useState } from 'react';
import { DashboardJob } from '../jobModel';

const NONE: ReadonlySet<string> = new Set();

export type SelectAllState = 'none' | 'some' | 'all';

/** Row selection scoped to the visible jobs: hidden or deleted jobs never count as selected. */
export function useJobSelection(visibleJobs: DashboardJob[]) {
  const [picked, setPicked] = useState<ReadonlySet<string>>(NONE);

  const selectedJobs = useMemo(() => visibleJobs.filter((j) => picked.has(j.id)), [visibleJobs, picked]);
  const selectedIds = useMemo(
    () => (selectedJobs.length === picked.size ? picked : new Set(selectedJobs.map((j) => j.id))),
    [selectedJobs, picked],
  );
  const selectAllState: SelectAllState =
    selectedJobs.length === 0 ? 'none' : selectedJobs.length === visibleJobs.length ? 'all' : 'some';

  const toggle = useCallback((id: string) => {
    setPicked((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });
  }, []);

  const clear = useCallback(() => setPicked(NONE), []);

  const toggleAll = useCallback(() => {
    setPicked(selectAllState === 'all' ? NONE : new Set(visibleJobs.map((j) => j.id)));
  }, [selectAllState, visibleJobs]);

  return { selectedIds, selectedJobs, selectAllState, toggle, toggleAll, clear };
}
